import * as THREE from 'three';
import type { Player } from '@entities/Player';
import type { Enemy } from '@entities/Enemy';
import type { TargetingSystem } from './TargetingSystem';
import type { SkillDefinition, ActiveSkill, Buff, CombatLog, SkillEffect } from 'shared';
import { SKILLS, DEFAULT_ACTION_BAR } from 'shared';

export interface SkillSystemEvents {
  onSkillUsed?: (skillId: string, target?: Enemy) => void;
  onCooldownComplete?: (skillId: string) => void;
  onBuffApplied?: (buff: Buff) => void;
  onBuffRemoved?: (buffId: string) => void;
  onCombatLog?: (log: CombatLog) => void;
  onDamageDealt?: (amount: number, position: THREE.Vector3, isCrit: boolean, damageType: string) => void;
  onHealingDone?: (amount: number, position: THREE.Vector3, isCrit: boolean) => void;
  onCastStart?: (skillId: string, castTime: number) => void;
  onCastComplete?: (skillId: string) => void;
  onCastInterrupt?: () => void;
}

export class SkillSystem {
  private player: Player;
  private targetingSystem: TargetingSystem;
  private camera: THREE.Camera;

  // Player stats (will be tied to character system later)
  private playerStats = {
    health: 100,
    maxHealth: 100,
    mana: 100,
    maxMana: 100,
    attackPower: 10,
    spellPower: 10,
    critChance: 0.1,
    level: 1,
    class: 'warrior',
  };

  // Skill state
  private actionBar: string[] = [];
  private cooldowns: Map<string, number> = new Map(); // skillId -> cooldown remaining (ms)
  private globalCooldown: number = 0;
  private globalCooldownDuration: number = 1500; // 1.5 seconds GCD

  // Casting
  private isCasting: boolean = false;
  private castingSkillId: string | null = null;
  private castStartTime: number = 0;
  private castDuration: number = 0;

  // Buffs/Debuffs
  private buffs: Map<string, Buff> = new Map();
  private buffIdCounter: number = 0;

  // Combat log
  private combatLog: CombatLog[] = [];
  private maxCombatLogSize: number = 100;

  // Events
  public events: SkillSystemEvents = {};

  // Damage numbers container
  private damageContainer: HTMLDivElement;

  constructor(
    player: Player,
    targetingSystem: TargetingSystem,
    camera: THREE.Camera,
    playerClass: string = 'warrior'
  ) {
    this.player = player;
    this.targetingSystem = targetingSystem;
    this.camera = camera;
    this.playerStats.class = playerClass;

    // Set up action bar for class
    this.actionBar = [...(DEFAULT_ACTION_BAR[playerClass] || DEFAULT_ACTION_BAR.default)];

    // Create damage numbers container
    this.damageContainer = document.createElement('div');
    this.damageContainer.id = 'skill-damage-numbers';
    this.damageContainer.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      overflow: hidden;
      z-index: 55;
    `;
    document.getElementById('game-container')?.appendChild(this.damageContainer);
  }

  public update(deltaTime: number): void {
    const now = Date.now();
    const deltMs = deltaTime * 1000;

    // Update global cooldown
    if (this.globalCooldown > 0) {
      this.globalCooldown = Math.max(0, this.globalCooldown - deltMs);
    }

    // Update skill cooldowns
    for (const [skillId, remaining] of this.cooldowns.entries()) {
      const newRemaining = remaining - deltMs;
      if (newRemaining <= 0) {
        this.cooldowns.delete(skillId);
        this.events.onCooldownComplete?.(skillId);
      } else {
        this.cooldowns.set(skillId, newRemaining);
      }
    }

    // Update casting
    if (this.isCasting && this.castingSkillId) {
      const castProgress = (now - this.castStartTime) / this.castDuration;
      if (castProgress >= 1) {
        this.completeCast();
      }
    }

    // Update buffs
    for (const [buffId, buff] of this.buffs.entries()) {
      buff.remainingDuration -= deltMs;

      // Apply DoT/HoT ticks
      if (buff.effect.tickRate && buff.effect.type === 'dot') {
        // DoT damage is handled separately
      } else if (buff.effect.tickRate && buff.effect.type === 'hot') {
        // HoT healing is handled separately
      }

      if (buff.remainingDuration <= 0) {
        this.removeBuff(buffId);
      }
    }

    // Mana regeneration (2% per second out of combat)
    if (!this.isCasting) {
      this.playerStats.mana = Math.min(
        this.playerStats.maxMana,
        this.playerStats.mana + (this.playerStats.maxMana * 0.02 * deltaTime)
      );
    }
  }

  public useSkillBySlot(slotIndex: number, enemies: Enemy[]): boolean {
    if (slotIndex < 0 || slotIndex >= this.actionBar.length) {
      return false;
    }

    const skillId = this.actionBar[slotIndex];
    if (!skillId) {
      return false;
    }

    return this.useSkill(skillId, enemies);
  }

  public useSkill(skillId: string, enemies: Enemy[]): boolean {
    const skill = SKILLS[skillId];
    if (!skill) {
      console.warn(`Skill not found: ${skillId}`);
      return false;
    }

    // Check if we can use the skill
    if (!this.canUseSkill(skillId)) {
      return false;
    }

    // Get target if needed
    const target = this.getTargetForSkill(skill, enemies);
    if (skill.targetType === 'enemy' && !target) {
      console.log('No valid target for skill');
      return false;
    }

    // Check range
    if (target && skill.range > 0) {
      const distance = this.player.getPosition().distanceTo(target.getPosition());
      if (distance > skill.range) {
        console.log(`Target out of range (${distance.toFixed(1)} > ${skill.range})`);
        return false;
      }
    }

    // Consume mana
    this.playerStats.mana -= skill.manaCost;

    // Start cast if needed
    if (skill.castTime > 0) {
      this.startCast(skillId, skill.castTime * 1000, target || undefined);
      return true;
    }

    // Instant cast - execute immediately
    this.executeSkill(skill, target || undefined, enemies);
    return true;
  }

  private canUseSkill(skillId: string): boolean {
    const skill = SKILLS[skillId];
    if (!skill) return false;

    // Check if casting
    if (this.isCasting) {
      console.log('Cannot use skill while casting');
      return false;
    }

    // Check global cooldown
    if (this.globalCooldown > 0) {
      console.log('Global cooldown active');
      return false;
    }

    // Check skill cooldown
    if (this.cooldowns.has(skillId)) {
      console.log(`Skill ${skillId} on cooldown`);
      return false;
    }

    // Check mana
    if (this.playerStats.mana < skill.manaCost) {
      console.log('Not enough mana');
      return false;
    }

    // Check level
    if (this.playerStats.level < skill.requiredLevel) {
      console.log('Level requirement not met');
      return false;
    }

    // Check class
    if (skill.requiredClass && !skill.requiredClass.includes(this.playerStats.class)) {
      console.log('Class requirement not met');
      return false;
    }

    return true;
  }

  private getTargetForSkill(skill: SkillDefinition, enemies: Enemy[]): Enemy | null {
    switch (skill.targetType) {
      case 'enemy':
        return this.targetingSystem.getTarget();
      case 'self':
      case 'ally':
        return null; // Self-targeting handled differently
      case 'ground':
      case 'cone':
      case 'aoe':
        // For AoE, we might need a target position instead
        return this.targetingSystem.getTarget();
      default:
        return null;
    }
  }

  private startCast(skillId: string, duration: number, target?: Enemy): void {
    this.isCasting = true;
    this.castingSkillId = skillId;
    this.castStartTime = Date.now();
    this.castDuration = duration;

    this.events.onCastStart?.(skillId, duration);
    console.log(`Starting cast: ${skillId} (${duration}ms)`);
  }

  private completeCast(): void {
    if (!this.castingSkillId) return;

    const skill = SKILLS[this.castingSkillId];
    const skillId = this.castingSkillId;

    this.isCasting = false;
    this.castingSkillId = null;

    if (skill) {
      const target = this.targetingSystem.getTarget();
      this.executeSkill(skill, target || undefined, []);
    }

    this.events.onCastComplete?.(skillId);
  }

  public interruptCast(): void {
    if (this.isCasting) {
      this.isCasting = false;
      this.castingSkillId = null;
      this.events.onCastInterrupt?.();
      console.log('Cast interrupted!');
    }
  }

  private executeSkill(skill: SkillDefinition, target: Enemy | undefined, enemies: Enemy[]): void {
    console.log(`Executing skill: ${skill.name}`);

    // Apply cooldowns
    if (skill.cooldown > 0) {
      this.cooldowns.set(skill.id, skill.cooldown * 1000);
    }
    this.globalCooldown = this.globalCooldownDuration;

    // Process effects
    for (const effect of skill.effects) {
      this.processEffect(effect, skill, target, enemies);
    }

    // Fire event
    this.events.onSkillUsed?.(skill.id, target);

    // Add to combat log
    this.addCombatLog({
      timestamp: Date.now(),
      type: skill.type === 'heal' ? 'heal' : 'damage',
      sourceId: 'player',
      sourceName: 'You',
      targetId: target ? 'enemy' : 'self',
      targetName: target ? target.getName() : 'You',
      skillId: skill.id,
      skillName: skill.name,
    });
  }

  private processEffect(
    effect: SkillEffect,
    skill: SkillDefinition,
    target: Enemy | undefined,
    enemies: Enemy[]
  ): void {
    switch (effect.type) {
      case 'damage':
        this.processDamage(effect, skill, target, enemies);
        break;
      case 'heal':
        this.processHeal(effect, skill);
        break;
      case 'buff':
        this.processBuff(effect, skill, false);
        break;
      case 'debuff':
        if (target) {
          this.processDebuff(effect, skill, target);
        }
        break;
      case 'dot':
        if (target) {
          this.processDot(effect, skill, target);
        }
        break;
      case 'hot':
        this.processHot(effect, skill);
        break;
    }
  }

  private processDamage(
    effect: SkillEffect,
    skill: SkillDefinition,
    target: Enemy | undefined,
    enemies: Enemy[]
  ): void {
    // Determine targets based on skill type
    let targets: Enemy[] = [];

    if (skill.targetType === 'aoe' || skill.targetType === 'cone') {
      // AoE damage - hit all enemies in range
      const playerPos = this.player.getPosition();
      targets = enemies.filter(e => {
        if (!e.isAlive()) return false;
        const dist = playerPos.distanceTo(e.getPosition());
        return dist <= skill.range;
      });
    } else if (target) {
      targets = [target];
    }

    for (const t of targets) {
      this.dealDamage(t, effect.value, effect.damageType || 'physical', skill);
    }
  }

  private dealDamage(
    target: Enemy,
    baseDamage: number,
    damageType: string,
    skill: SkillDefinition
  ): void {
    // Calculate damage
    const isCrit = Math.random() < this.playerStats.critChance;
    const statBonus = damageType === 'physical'
      ? this.playerStats.attackPower * 0.1
      : this.playerStats.spellPower * 0.1;

    let damage = Math.floor(baseDamage + statBonus);

    if (isCrit) {
      damage = Math.floor(damage * 2);
    }

    // Apply variance
    damage = Math.floor(damage * (0.9 + Math.random() * 0.2));

    // Deal damage
    const killed = target.takeDamage(damage);

    // Show damage number
    this.showDamageNumber(damage, target.getPosition(), isCrit, damageType);

    // Fire event
    this.events.onDamageDealt?.(damage, target.getPosition(), isCrit, damageType);

    console.log(`${skill.name} dealt ${damage}${isCrit ? ' (CRIT!)' : ''} ${damageType} damage to ${target.getName()}`);

    if (killed) {
      console.log(`${target.getName()} was slain!`);
    }
  }

  private processHeal(effect: SkillEffect, skill: SkillDefinition): void {
    const isCrit = Math.random() < this.playerStats.critChance;
    let healAmount = effect.value + this.playerStats.spellPower * 0.15;

    if (isCrit) {
      healAmount *= 1.5;
    }

    healAmount = Math.floor(healAmount);

    this.playerStats.health = Math.min(
      this.playerStats.maxHealth,
      this.playerStats.health + healAmount
    );

    // Show healing number
    this.showHealNumber(healAmount, this.player.getPosition(), isCrit);

    this.events.onHealingDone?.(healAmount, this.player.getPosition(), isCrit);
    console.log(`${skill.name} healed for ${healAmount}${isCrit ? ' (CRIT!)' : ''}`);
  }

  private processBuff(effect: SkillEffect, skill: SkillDefinition, isDebuff: boolean): void {
    const buffId = `buff_${this.buffIdCounter++}`;

    const buff: Buff = {
      id: buffId,
      skillId: skill.id,
      name: skill.name,
      icon: skill.icon,
      effect: effect,
      remainingDuration: (effect.duration || 0) * 1000,
      totalDuration: (effect.duration || 0) * 1000,
      stacks: 1,
      maxStacks: effect.stat === 'poisonDamage' ? 5 : 1,
    };

    this.buffs.set(buffId, buff);
    this.events.onBuffApplied?.(buff);

    console.log(`Buff applied: ${skill.name} (+${effect.value} ${effect.stat}) for ${effect.duration}s`);
  }

  private processDebuff(effect: SkillEffect, skill: SkillDefinition, target: Enemy): void {
    // For now, debuffs are tracked on enemies (simplified)
    console.log(`Debuff applied to ${target.getName()}: ${effect.stat} for ${effect.duration}s`);
  }

  private processDot(effect: SkillEffect, skill: SkillDefinition, target: Enemy): void {
    // DoT is applied as a debuff that ticks
    console.log(`DoT applied to ${target.getName()}: ${effect.value} ${effect.damageType} every ${effect.tickRate}s`);
  }

  private processHot(effect: SkillEffect, skill: SkillDefinition): void {
    // HoT is applied as a buff that ticks
    const buffId = `hot_${this.buffIdCounter++}`;

    const buff: Buff = {
      id: buffId,
      skillId: skill.id,
      name: skill.name,
      icon: skill.icon,
      effect: effect,
      remainingDuration: (effect.duration || 0) * 1000,
      totalDuration: (effect.duration || 0) * 1000,
    };

    this.buffs.set(buffId, buff);
    this.events.onBuffApplied?.(buff);

    console.log(`HoT applied: ${skill.name} (${effect.value} every ${effect.tickRate}s) for ${effect.duration}s`);
  }

  private removeBuff(buffId: string): void {
    if (this.buffs.has(buffId)) {
      this.buffs.delete(buffId);
      this.events.onBuffRemoved?.(buffId);
    }
  }

  private showDamageNumber(
    value: number,
    position: THREE.Vector3,
    isCrit: boolean,
    damageType: string
  ): void {
    const colors: Record<string, string> = {
      physical: '#fff',
      fire: '#ff6600',
      ice: '#00ccff',
      lightning: '#ffff00',
      arcane: '#cc66ff',
      holy: '#ffff99',
      shadow: '#9933ff',
    };

    const color = colors[damageType] || '#fff';

    const element = document.createElement('div');
    element.textContent = value.toString();
    element.style.cssText = `
      position: absolute;
      font-family: 'Segoe UI', Arial, sans-serif;
      font-weight: bold;
      font-size: ${isCrit ? '32px' : '22px'};
      color: ${isCrit ? '#ff0' : color};
      text-shadow: 2px 2px 4px rgba(0,0,0,0.9), 0 0 ${isCrit ? '15px' : '8px'} ${color};
      pointer-events: none;
      transform: translate(-50%, -50%);
      z-index: 56;
      transition: opacity 0.5s;
    `;

    this.damageContainer.appendChild(element);

    // Animate
    const startPos = position.clone().add(new THREE.Vector3(
      (Math.random() - 0.5) * 0.5,
      2,
      (Math.random() - 0.5) * 0.5
    ));

    const animate = () => {
      startPos.y += 0.03;
      const screenPos = this.worldToScreen(startPos);

      if (screenPos) {
        element.style.left = `${screenPos.x}px`;
        element.style.top = `${screenPos.y}px`;
      }
    };

    // Initial position
    animate();

    // Animate upward
    let frame = 0;
    const animInterval = setInterval(() => {
      frame++;
      animate();
      if (frame > 30) {
        element.style.opacity = '0';
      }
      if (frame > 50) {
        clearInterval(animInterval);
        element.remove();
      }
    }, 16);
  }

  private showHealNumber(value: number, position: THREE.Vector3, isCrit: boolean): void {
    const element = document.createElement('div');
    element.textContent = `+${value}`;
    element.style.cssText = `
      position: absolute;
      font-family: 'Segoe UI', Arial, sans-serif;
      font-weight: bold;
      font-size: ${isCrit ? '28px' : '20px'};
      color: #00ff00;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.9), 0 0 10px rgba(0,255,0,0.5);
      pointer-events: none;
      transform: translate(-50%, -50%);
      z-index: 56;
    `;

    this.damageContainer.appendChild(element);

    const startPos = position.clone().add(new THREE.Vector3(0, 2.5, 0));

    let frame = 0;
    const animInterval = setInterval(() => {
      frame++;
      startPos.y += 0.02;
      const screenPos = this.worldToScreen(startPos);

      if (screenPos) {
        element.style.left = `${screenPos.x}px`;
        element.style.top = `${screenPos.y}px`;
      }

      if (frame > 30) {
        element.style.opacity = String(1 - (frame - 30) / 30);
      }

      if (frame > 60) {
        clearInterval(animInterval);
        element.remove();
      }
    }, 16);
  }

  private worldToScreen(position: THREE.Vector3): { x: number; y: number } | null {
    const vector = position.clone();
    vector.project(this.camera);

    if (vector.z > 1) return null;

    return {
      x: (vector.x * 0.5 + 0.5) * window.innerWidth,
      y: (-vector.y * 0.5 + 0.5) * window.innerHeight,
    };
  }

  private addCombatLog(log: CombatLog): void {
    this.combatLog.push(log);
    if (this.combatLog.length > this.maxCombatLogSize) {
      this.combatLog.shift();
    }
    this.events.onCombatLog?.(log);
  }

  // Public getters
  public getActionBar(): string[] {
    return [...this.actionBar];
  }

  public setActionBarSlot(slotIndex: number, skillId: string): void {
    if (slotIndex >= 0 && slotIndex < 6) {
      this.actionBar[slotIndex] = skillId;
    }
  }

  public getCooldown(skillId: string): number {
    return this.cooldowns.get(skillId) || 0;
  }

  public getCooldownPercent(skillId: string): number {
    const skill = SKILLS[skillId];
    if (!skill || skill.cooldown === 0) return 0;

    const remaining = this.cooldowns.get(skillId) || 0;
    return remaining / (skill.cooldown * 1000);
  }

  public getGlobalCooldownPercent(): number {
    return this.globalCooldown / this.globalCooldownDuration;
  }

  public getBuffs(): Buff[] {
    return Array.from(this.buffs.values());
  }

  public isCastingSkill(): boolean {
    return this.isCasting;
  }

  public getCastProgress(): number {
    if (!this.isCasting) return 0;
    return (Date.now() - this.castStartTime) / this.castDuration;
  }

  public getCastingSkill(): SkillDefinition | null {
    if (!this.castingSkillId) return null;
    return SKILLS[this.castingSkillId] || null;
  }

  public getPlayerStats() {
    return { ...this.playerStats };
  }

  public setPlayerStats(stats: Partial<typeof this.playerStats>): void {
    Object.assign(this.playerStats, stats);
  }

  public getCombatLog(): CombatLog[] {
    return [...this.combatLog];
  }

  public getSkillInfo(skillId: string): SkillDefinition | undefined {
    return SKILLS[skillId];
  }

  public heal(amount: number): void {
    const oldHealth = this.playerStats.health;
    this.playerStats.health = Math.min(this.playerStats.maxHealth, this.playerStats.health + amount);
    const actualHeal = this.playerStats.health - oldHealth;
    if (actualHeal > 0) {
      this.events.onHealingDone?.(actualHeal, this.player.mesh.position.clone(), false, 'heal');
    }
  }

  public restoreMana(amount: number): void {
    this.playerStats.mana = Math.min(this.playerStats.maxMana, this.playerStats.mana + amount);
  }

  public takeDamage(amount: number): void {
    this.playerStats.health = Math.max(0, this.playerStats.health - amount);
  }

  public destroy(): void {
    this.damageContainer.remove();
  }
}
